import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Dropdown, Form, Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import { DisplayImage } from '../../widgets/components/General/DisplayImage'
import { useAuth } from '../../auth'
import { cancelDocument, getDocumentsApi, getDocumentSubmissionApi } from '../core/_requests'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { useTimeFormat } from '../../../hooks/useTimeFormat'
import { DocumentSubmissionStatus, SignatureTypes } from '../core/_constants'
import { useDigitalSigning } from '../core/DigitalSigningContext'
import NoDataPage from '../../widgets/components/NoDataPage'
import DocumentViewModal from '../view/DocumentViewModal'
import { toast } from 'react-toastify'
import SignInModal from './SignInModal'
import { DocumentDetails } from '../core/_models'
import SkeletonRow from './SkeletonRow'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { CancelDocumentConfirmationModal } from './CancelDocumentConfirmationModal'
import { removeNullFields } from '../../../core/_util'

const AllDocuments = ({
  documentsSearch,
  setCurrentDocumentStep,
  isDocumentCreate,
  submittersDetails,
  setSubmittersDetails,
  setShowDocumentModal,
  getDocumentsDashboardCounts,
  setIsDocumentCreate,
  viewDocumentLoading,
  setViewDocumentLoading,
  getDashboardCounts,
  setIsDuplicateDocument,
  setDocusealId,
  setTemplateId,
}: {
  documentsSearch: string
  setCurrentDocumentStep: Dispatch<SetStateAction<number>>
  isDocumentCreate: boolean
  submittersDetails: any
  setSubmittersDetails: Dispatch<SetStateAction<any>>
  setShowDocumentModal: Dispatch<SetStateAction<boolean>>
  getDocumentsDashboardCounts: () => {}
  setIsDocumentCreate: Dispatch<SetStateAction<boolean>>
  viewDocumentLoading: boolean
  setViewDocumentLoading: Dispatch<SetStateAction<boolean>>
  setIsDuplicateDocument: Dispatch<SetStateAction<boolean>>
  getDashboardCounts: () => void
  setDocusealId: Dispatch<SetStateAction<number | undefined>>
  setTemplateId: Dispatch<SetStateAction<number | undefined>>
}) => {
  const { formatMessage } = useIntl()
  const [totalPages, setTotalPages] = useState(0)
  const [allDocumentCurrentPage, setAllDocumentCurrentPage] = useState<number>(1)
  const { currentUser, companyId } = useAuth()
  const [limitPerPage, setLimitPerPage] = useState(10)
  const [documents, setDocuments] = useState<DocumentDetails[] | []>([])
  const [documentsTotal, setDocumentsTotal] = useState(0)
  const didMount = useRef(false)
  const { getDateValue } = useDateFormat()
  const { getTimeValue } = useTimeFormat()
  const { selectedDocumentStatusFilter, selectedDocumentSignatureStatusFilter } = useDigitalSigning()
  const [showDocumentViewModal, setShowDocumentViewModal] = useState(false)
  const [token, setToken] = useState()
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [submissionId, setSubmissionId] = useState<number>()
  const [cancelLoading, setCancelLoading] = useState(false)
  const [showSignInModal, setShowSignInModal] = useState(false)
  const [signUrl, setSignUrl] = useState('')
  const [signatoryEmail, setSignatoryEmail] = useState('')
  const [signatoryId, setSignatoryId] = useState<number | undefined>()
  const [loading, setLoading] = useState(false)
  const [signatoryLoading, setSignatoryLoading] = useState(false)
  const [isViewModalTriggered, setIsViewModalTriggered] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [documentName, setDocumentName] = useState('')

  async function getDocuments() {
    setLoading(true)
    try {
      const {
        data: { data },
      } = await getDocumentsApi(
        companyId!,
        allDocumentCurrentPage,
        limitPerPage,
        documentsSearch,
        selectedDocumentStatusFilter,
        selectedDocumentSignatureStatusFilter
      )

      setDocuments(data.documents)
      setDocumentsTotal(data.total)
      setIsDocumentCreate(false)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setTotalPages(Math.ceil(documentsTotal / limitPerPage))
  }, [documentsTotal, limitPerPage])

  useEffect(() => {
    let debounceTimeout: NodeJS.Timeout | null = null
    if (didMount.current) {
      setAllDocumentCurrentPage(1)
      if (debounceTimeout) clearTimeout(debounceTimeout)
      debounceTimeout = setTimeout(() => {
        getDocuments()
      }, 1000)
      return () => {
        if (debounceTimeout) clearTimeout(debounceTimeout)
      }
    } else didMount.current = true
  }, [documentsSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getDocuments()
    // eslint-disable-next-line
  }, [
    selectedDocumentStatusFilter,
    selectedDocumentSignatureStatusFilter,
    allDocumentCurrentPage,
    limitPerPage,
    isDocumentCreate,
  ])

  const renderPaginationItems = () => {
    const items = []
    const visiblePageCount = 5 // Number of visible page numbers

    // Calculate the range of visible pages based on the current page
    let startPage = Math.max(1, allDocumentCurrentPage - Math.floor(visiblePageCount / 2))
    let endPage = Math.min(totalPages, startPage + visiblePageCount - 1)

    // Adjust the range if it exceeds the total number of pages
    if (endPage - startPage + 1 < visiblePageCount) {
      startPage = Math.max(1, endPage - visiblePageCount + 1)
    }

    // Add the First and Prev buttons
    items.push(
      <Pagination.First
        key='first'
        disabled={allDocumentCurrentPage === 1}
        onClick={() => handleClick(1)}
      />
    )
    items.push(
      <Pagination.Prev
        key='prev'
        disabled={allDocumentCurrentPage === 1}
        onClick={() => handleClick(allDocumentCurrentPage - 1)}
      />
    )

    // Add the page numbers and Ellipsis as needed
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === allDocumentCurrentPage}
          onClick={() => handleClick(page)}
        >
          {page}
        </Pagination.Item>
      )
    }

    if (startPage > 1) {
      items.splice(2, 0, <Pagination.Ellipsis key='ellipsis1' />)
    }
    if (endPage < totalPages) {
      items.splice(items.length, 0, <Pagination.Ellipsis key='ellipsis2' />)
    }

    // Add the Next and Last buttons
    items.push(
      <Pagination.Next
        key='next'
        disabled={allDocumentCurrentPage === totalPages}
        onClick={() => handleClick(allDocumentCurrentPage + 1)}
      />
    )
    items.push(
      <Pagination.Last
        key='last'
        disabled={allDocumentCurrentPage === totalPages}
        onClick={() => handleClick(totalPages)}
      />
    )

    return items
  }

  const handleClick = (pageNumber: number) => {
    setAllDocumentCurrentPage(pageNumber)
  }

  const handleViewDocument = async (submissionId: number) => {
    setViewDocumentLoading(true)
    try {
      const {
        data: { data },
      } = await getDocumentSubmissionApi(submissionId, companyId!)
      if (data) {
        setToken(data.token)
        setSubmittersDetails(data.submissionDetails)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setViewDocumentLoading(false)
    }
  }

  const handleCancelDocument = async () => {
    setCancelLoading(true)
    try {
      const {
        data: { data },
      } = await cancelDocument(submissionId!, companyId!)
      if (data) {
        toast.success(formatMessage({ id: 'Document Cancelled successfully' }))
        await getDocuments()
        await getDocumentsDashboardCounts()
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DigitalSigningDocumentDeleted,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              submission_id: submissionId,
            },
          },
        })
      }
    } catch (error) {
      console.log(error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.DigitalSigningDocumentDeleteFailed,
          action: 'normal',
          metadata: {
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            submission_id: submissionId,
            error: error,
          },
        },
      })
    } finally {
      setCancelLoading(false)
      setShowCancelModal(false)
    }
  }

  return (
    <>
      <div className='card mt-5'>
        <div className='rounded bg-white p-6 position-relative'>
          {!loading && documents?.length === 0 ? (
            <div className='my-5 mx-6'>
              <NoDataPage width={'w-0'} />
            </div>
          ) : (
            <div className='table table-responsive h-100 '>
              <Table className='w-100'>
                <thead className='custom-thead border-bottom pb-4'>
                  <tr className='font-size-12 font-weight-400 table-row border-dark-mode'>
                    <th className='min-w-120px pb-8 pt-0'>{formatMessage({ id: 'Title' })}</th>
                    <th className='min-w-120px pb-8 pt-0'>
                      {formatMessage({ id: 'Your Signature' })}
                    </th>
                    <th className='min-w-120px pb-8 pt-0'>{formatMessage({ id: 'Signatories' })}</th>
                    <th className='min-w-120px pb-8 pt-0'>
                      {formatMessage({ id: 'Signed Recipients' })}
                    </th>
                    <th className='min-w-120px pb-8 pt-0'>{formatMessage({ id: 'Validity' })}</th>
                    <th className='min-w-120px pb-8 pt-0'>{formatMessage({ id: 'Status' })}</th>
                    <th className='min-w-120px d-flex justify-content-center border-0 pb-8 pt-0'>
                      {formatMessage({ id: 'Actions' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <>
                      <SkeletonRow count={8} />
                    </>
                  ) : (
                    <>
                      {documents?.map((document: DocumentDetails, index: number) => (
                        <tr
                          className='cursor-pointer'
                          key={document.id}
                          onClick={() => {
                            setSubmissionId(document.id)
                            setDocumentName(document.title)
                            handleViewDocument(document.id)
                            if (document.signatoryDetails) {
                              setSignatoryId(document.signatoryDetails.signatoryId)
                            }

                            setIsViewModalTriggered(true)
                            TagManager.dataLayer({
                              dataLayer: {
                                event: GTMEvent.DigitalSigningDocumentViewedSuccessful,
                                metadata: removeNullFields({
                                  document_name: document.title,
                                  viewer_name: `${currentUser?.firstName} ${currentUser?.lastName}`,
                                  viewer_email: currentUser?.email,
                                  company_id: companyId,
                                  user_id: currentUser?.userId,
                                }),
                              },
                            })
                            setShowDocumentViewModal(true)
                          }}
                        >
                          <td className='pt-7 d-flex align-items-center'>
                            <div className='d-flex align-items-center'>
                              <div
                                style={{
                                  borderLeft: `3px solid ${document.status === DocumentSubmissionStatus.OVERDUE ||
                                    document.status === DocumentSubmissionStatus.EXPIRED
                                    ? '#F1416C'
                                    : document.status === DocumentSubmissionStatus.AWAITING
                                      ? '#FFA800'
                                      : document.status === DocumentSubmissionStatus.CANCELLED
                                        ? '#7E8299'
                                        : '#50CD89'
                                    }`,
                                }}
                              >
                                <div className='ps-7'>
                                  <div className='font-weight-500 font-size-16 title-width-mobile'>
                                    {document.title}
                                  </div>
                                  <div>
                                    {getDateValue(
                                      new Date(document.createdAt).toISOString()?.toString()
                                    )}{' '}
                                    |{' '}
                                    {getTimeValue(
                                      new Date(document.createdAt)
                                        .toISOString()
                                        ?.split('T')[1]
                                        .split('.')[0]
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className='pt-9'>
                            {document?.signatureStatus &&
                              document.status !== DocumentSubmissionStatus.EXPIRED ? (
                              <div
                                className={`badge ${document.signatureStatus === SignatureTypes.WAITING_FOR_YOU
                                  ? 'badge-success'
                                  : 'badge-primary'
                                  } py-2 px-6`}
                              >
                                {document.signatureStatus}
                              </div>
                            ) : (
                              <div className='ms-10 font-weight-500 font-size-16 d-flex justify-content-between'>
                                -
                              </div>
                            )}
                          </td>
                          <td className='pt-7'>
                            <div className='d-flex pb-2'>
                              <div className='d-flex'>
                                {document.signatories
                                  .slice(0, 5)
                                  .map(({ id, name }: { id: number; name: string }) => (
                                    <DisplayImage
                                      key={id}
                                      imgName={name}
                                      isUserAvatarWithLetter={true}
                                      alt={formatMessage({ id: 'profile' })}
                                      fit='contain'
                                      className='c-profile'
                                    />
                                  ))}
                              </div>
                              {document.signatories.length > 5 && (
                                <div className='align-items-lg-center d-flex mb-4 ps-3 pt-1 '>
                                  +{document.signatories.length - 5} {formatMessage({ id: 'more' })}
                                </div>
                              )}
                            </div>

                            <p className='mb-0 font-size-13'>
                              {formatMessage({ id: 'Signatories' })}
                            </p>
                          </td>
                          <td className='pt-7'>
                            <p className='mb-1 font-size-16 font-weight-500 text-width-mobile'>
                              {document.signedCount}/{document.totalSignatoryCount}
                            </p>
                            <p className='mb-0 font-size-13'>{formatMessage({ id: 'Signed' })}</p>
                          </td>
                          <td className='pt-7'>
                            {typeof document.validity === 'number' ||
                              typeof document.validity === 'string' ? (
                              <>
                                {document.validity === 'No Expiry' ? (
                                  <p className='mb-0 mt-3 font-size-13 text-width-mobile'>
                                    {document.validity}
                                  </p>
                                ) : (
                                  <>
                                    {document.validity === 'Expired' ? (
                                      <p className='mb-0 font-size-16 font-weight-500 text-width-mobile'>
                                        -
                                      </p>
                                    ) : (
                                      <p className='mb-1 font-size-16 text-width-mobile'>
                                        {document.validity}
                                      </p>
                                    )}

                                    <p className='mb-0 font-size-13'>
                                      {formatMessage({ id: 'Days Left' })}
                                    </p>
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                <p className='mb-0 font-size-16 font-weight-500'>-</p>
                                {document.status === DocumentSubmissionStatus.OVERDUE ? (
                                  <p className='mb-0 font-size-13 '>
                                    {formatMessage({ id: 'Days Overdue' })}
                                  </p>
                                ) : (
                                  <p className='mb-0 font-size-13 '>
                                    {formatMessage({ id: 'Days Left' })}
                                  </p>
                                )}
                              </>
                            )}
                          </td>
                          <td className='pt-9'>
                            <div
                              className={`badge ${document.status === DocumentSubmissionStatus.OVERDUE ||
                                document.status === DocumentSubmissionStatus.EXPIRED
                                ? 'badge-light-danger'
                                : document.status === DocumentSubmissionStatus.COMPLETED
                                  ? 'badge-light-success'
                                  : document.status === DocumentSubmissionStatus.CANCELLED
                                    ? 'badge-light-secondary text-clr8898'
                                    : 'badge-light-warning'
                                } py-2 px-6`}
                            >
                              {document.status}
                            </div>
                          </td>

                          <td className='pt-8' onClick={(e) => e.stopPropagation()}>
                            <div className='dropdown-container d-flex justify-content-center'>
                              <Dropdown
                                show={index === selectedIndex}
                                onToggle={(isOpen, event: any) => {
                                  if (event.source === 'click') {
                                    setSelectedIndex(isOpen ? index : undefined)
                                  } else {
                                    setSelectedIndex(undefined)
                                  }
                                }}
                              >
                                <Dropdown.Toggle className='btn btn-white table-three-dots dots-container '>
                                  <img src={threeDots} alt='' className='three-dots-image' />
                                </Dropdown.Toggle>

                                <Dropdown.Menu className='m-2'>
                                  <Dropdown.Item
                                    className='text-dark ps-5'
                                    onClick={() => {
                                      setIsViewModalTriggered(true)
                                      setDocumentName(document.title)
                                      TagManager.dataLayer({
                                        dataLayer: {
                                          event: GTMEvent.DigitalSigningDocumentViewedSuccessful,
                                          metadata: {
                                            document_name: document.title,
                                            viewer_name: `${currentUser?.firstName} ${currentUser?.lastName}`,
                                            viewer_email: currentUser?.email,
                                            company_id: companyId,
                                            user_id: currentUser?.userId,
                                          },
                                        },
                                      })
                                      if (document.signatoryDetails) {
                                        setSignatoryId(document.signatoryDetails.signatoryId)
                                      }

                                      setShowDocumentViewModal(true)
                                      setSubmissionId(document.id)
                                      handleViewDocument(document.id)
                                    }}
                                  >
                                    <p className='mb-0'>{formatMessage({ id: 'View Document' })}</p>
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className='text-dark ps-5'
                                    onClick={async () => {
                                      setTemplateId(document.templateId)
                                      setDocusealId(document.templateDocusealId)
                                      setCurrentDocumentStep(1)
                                      setIsDuplicateDocument(true)
                                      setShowDocumentModal(true)
                                      await handleViewDocument(document.id)
                                    }}
                                  >
                                    {formatMessage({ id: 'Duplicate Document' })}
                                  </Dropdown.Item>
                                  {document.signatoryDetails &&
                                    (document.validity === 'No Expiry' ||
                                      typeof document.validity === 'number') && (
                                      <Dropdown.Item
                                        className='ps-5'
                                        onClick={() => {
                                          if (document.signatoryDetails) {
                                            setSignatoryId(document.signatoryDetails.signatoryId)
                                          }
                                          setDocumentName(document.title)
                                          setSignatoryEmail(
                                            document.signatoryDetails.signatoryEmail
                                          )
                                          setSignUrl(document.signatoryDetails.docusealSlug)
                                          setShowSignInModal(true)
                                        }}
                                      >
                                        {formatMessage({ id: 'Sign Document Now' })}
                                      </Dropdown.Item>
                                    )}
                                  <Dropdown.Item
                                    className='ps-5'
                                    onClick={() => {
                                      if (document.docusealPdfUrl) {
                                        TagManager.dataLayer({
                                          dataLayer: {
                                            event:
                                              GTMEvent.DigitalSigningDocumentDownloadedSuccessful,
                                            metadata: removeNullFields({
                                              document_name: document.title,
                                              company_id: companyId,
                                              user_id: currentUser?.userId,
                                            }),
                                          },
                                        })
                                        window.open(document.docusealPdfUrl, '_blank', '')
                                      } else {
                                        TagManager.dataLayer({
                                          dataLayer: {
                                            event: GTMEvent.DigitalSigningDocumentDownloadedFailed,
                                            metadata: removeNullFields({
                                              document_name: document.title,
                                              company_id: companyId,
                                              user_id: currentUser?.userId,
                                            }),
                                          },
                                        })
                                      }
                                    }}
                                  >
                                    {formatMessage({ id: 'Download Document' })}
                                  </Dropdown.Item>
                                  {!(
                                    document.status === DocumentSubmissionStatus.COMPLETED ||
                                    document.status === DocumentSubmissionStatus.CANCELLED ||
                                    document.status === DocumentSubmissionStatus.EXPIRED
                                  ) && (
                                      <Dropdown.Item
                                        className='text-danger ps-5'
                                        onClick={() => {
                                          setShowCancelModal(true)
                                          setSubmissionId(document.id)
                                        }}
                                      >
                                        {formatMessage({ id: 'Cancel Document' })}
                                      </Dropdown.Item>
                                    )}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </Table>
            </div>
          )}
          {!loading && documents?.length > 0 && documentsTotal > 10 && (
            <div className='w-100 d-flex justify-content-between align-items-center flex-wrap flex-md-nowrap'>
              <div className='d-flex align-items-center w-100 w-md-50 mb-md-0 mb-4'>
                <Form.Select
                  aria-label='Default select example'
                  onChange={(e) => {
                    setLimitPerPage(parseInt(e.target.value))
                    setAllDocumentCurrentPage(1)
                  }}
                  defaultValue={limitPerPage}
                >
                  <option value={10}>{formatMessage({ id: '10' })}</option>
                  <option value={20}>{formatMessage({ id: '20' })}</option>
                  <option value={50}>{formatMessage({ id: '50' })}</option>
                  <option value={100}>{formatMessage({ id: '100' })}</option>
                </Form.Select>
                <span className='font-size-13 ms-3'>
                  {formatMessage({
                    id: `Showing 1 to ${documents?.length} of ${documentsTotal} records`,
                  })}
                </span>
              </div>
              <div>
                <Pagination>{renderPaginationItems()}</Pagination>
              </div>
            </div>
          )}
        </div>
      </div>
      {isViewModalTriggered && (
        <DocumentViewModal
          showDocumentViewModal={showDocumentViewModal}
          setShowDocumentViewModal={setShowDocumentViewModal}
          token={token}
          submitterDetails={submittersDetails}
          showSignInModal={showSignInModal}
          setShowSignInModal={setShowSignInModal}
          signUrl={signUrl}
          setSignUrl={setSignUrl}
          signatoryEmail={signatoryEmail}
          setSignatoryEmail={setSignatoryEmail}
          getDocuments={getDocuments}
          viewDocumentLoading={viewDocumentLoading}
          signatoryId={signatoryId as number}
          signatoryLoading={signatoryLoading}
          getDashboardCounts={getDashboardCounts}
          setIsViewModalTriggered={setIsViewModalTriggered}
          setSignatoryLoading={setSignatoryLoading}
          documentName={documentName}
        />
      )}

      <CancelDocumentConfirmationModal
        setDeleteShowModal={setShowCancelModal}
        showDeleteModal={showCancelModal}
        onDeleteSubmit={handleCancelDocument}
        deleteLoading={cancelLoading}
      />
      {signatoryId && (
        <SignInModal
          showSignInModal={showSignInModal}
          setShowSignInModal={setShowSignInModal}
          signUrl={signUrl}
          signatoryEmail={signatoryEmail}
          getDocuments={getDocuments}
          handleViewDocument={handleViewDocument}
          submissionId={submissionId}
          signatoryId={signatoryId}
          setSignatoryLoading={setSignatoryLoading}
          getDashboardCounts={getDashboardCounts}
          documentName={documentName}
        />
      )}
    </>
  )
}

export default AllDocuments
